import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';

const VALUES = [
  {
    icon: 'ri-movie-2-line',
    title: '内容多元',
    desc: '覆盖电影、电视剧、综艺等多类别高清影视内容，满足不同观众的观看偏好。',
  },
  {
    icon: 'ri-layout-grid-line',
    title: '分类清晰',
    desc: '以类型、地区、年份等多维度筛选，帮助你快速定位想看的内容。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '体验友好',
    desc: '简洁现代的界面与流畅的浏览动线，让发现好内容变得轻松愉快。',
  },
];

export default function About() {
  useSeo({
    title: '关于我们 - 桃子视频',
    description:
      '了解桃子视频：一家多元化的高清视频内容平台，汇聚电影、电视剧、综艺等丰富影视资源，致力于为用户提供轻松愉悦的内容发现体验。',
    keywords: '桃子视频,关于我们,高清视频平台,影视内容',
  });

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 md:px-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <header className="mt-5 overflow-hidden rounded-xl border border-background-800/80 bg-gradient-to-br from-primary-900/35 via-background-900 to-background-900 p-6 md:p-10">
          <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-4xl">
            关于桃子视频
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-300 md:text-base">
            桃子视频是一家多元化的高清视频内容平台，面向热爱观看电影、电视剧、综艺节目的广大网络视频观众。
            我们以丰富的内容库、清晰的分类体系和贴心的浏览体验，帮助用户轻松发现感兴趣的热门视频，让每一次打开都充满期待。
          </p>
        </header>

        <section className="mt-8">
          <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
            我们的坚持
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {VALUES.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-800/80 bg-background-900 p-5"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-50">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-background-800/80 bg-background-900 p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground-50">
              <i className="ri-customer-service-2-line text-primary-400" />
              联系我们
            </h2>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3 text-foreground-300">
                <i className="ri-mail-line text-foreground-500" />
                意见反馈：
                <a
                  href="mailto:support@peach-video.example.com"
                  className="text-primary-400 transition-colors hover:text-primary-300"
                >
                  support@peach-video.example.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-foreground-300">
                <i className="ri-briefcase-line text-foreground-500" />
                商务合作：
                <a
                  href="mailto:business@peach-video.example.com"
                  className="text-primary-400 transition-colors hover:text-primary-300"
                >
                  business@peach-video.example.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-foreground-300">
                <i className="ri-map-pin-line text-foreground-500" />
                地址：北京市朝阳区内容产业园 A 座
              </li>
              <li className="flex items-center gap-3 text-foreground-300">
                <i className="ri-time-line text-foreground-500" />
                工作时间：周一至周五 9:00 - 18:00
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-800/80 bg-background-900 p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground-50">
              <i className="ri-shield-check-line text-primary-400" />
              版权与免责声明
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-400">
              本站为前端演示站点，页面中的视频标题、封面、简介等内容均为演示用虚拟数据，不代表任何真实影视作品。
              本站不提供也不包含任何真实的视频播放、下载或分发服务，所有“播放”相关按钮均为交互演示效果。
              如本站演示内容无意中与第三方权利产生冲突，请通过上方邮箱与我们联系，我们将及时处理。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400">
              本站所使用的图标、字体等资源均来自公开授权渠道。用户在使用本站过程中产生的一切行为，均应以合法合规为前提。
            </p>
          </div>
        </section>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
          <Link
            to="/search"
            className="whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
          >
            去搜索内容
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}